import { Device } from "api/types/device";
import { useEffect, useState } from "react";
import BounceLoader from "react-spinners/ClipLoader";
import { DeviceConfiguration } from "models/devices/configuration";
import { t } from "i18next";
import DeviceCard from "widgets/cards/device";
import { JsonViewer } from "widgets/shared/jsonViewer";
import { deviceGet, deviceSet, setSpecifics } from "api/device";
import { DeviceSetBody } from "api/types/device";
import GenericButton from "widgets/controls/buttons/generic";
import CircleButton from "widgets/controls/buttons/circle-button";
import { MdClose } from "react-icons/md";
import Switch from "widgets/shared/inputs/switch";
import useViewportDimensions from "hooks/viewport-dimensions";

interface GeneralProps {
  device?: Device;
  config?: DeviceConfiguration;
}

const DeviceRaw: React.FC<GeneralProps> = ({ device, config }) => {
  const { dimensions } = useViewportDimensions();
  const [loading, setLoading] = useState(false);
  const [jsonData, setJsonData] = useState({});
  const [specificsData, setSpecificsData] = useState({});
  const [pendingEdit, setPendingEdit] = useState<{
    updated_src: object;
    mode: "device" | "specifics";
  } | null>(null);
  const [selectedView, setSelectedView] = useState<"device" | "specifics">(
    "device"
  );

  const fetchData = async () => {
    setLoading(true);
    const { data, status, isLoading, error } = await deviceGet(
      device?.snr,
      true
    );
    setLoading(isLoading);

    if (!error && status && status === 200) {
      const { specifics, ...rest } = data; // Extract specifics from response
      setJsonData(rest);
      setSpecificsData(specifics || {});
    }

    setLoading(false);
  };

  useEffect(() => {
    if (device?.snr) {
      fetchData();
    }
  }, [device]);

  const applyEdits = async () => {
    if (!pendingEdit || !device?.snr) return;
    setLoading(true);
    try {
      if (pendingEdit.mode === "specifics") {
        const { error } = await setSpecifics(
          device.snr,
          pendingEdit.updated_src
        );
        if (!error) {
          setSpecificsData(pendingEdit.updated_src || {});
          setPendingEdit(null);
        }
      } else {
        const body = {
          ...(pendingEdit.updated_src as Record<string, unknown>),
          snr: device.snr,
        } as DeviceSetBody;
        const { error } = await deviceSet(body);
        if (!error) {
          await fetchData();
          setPendingEdit(null);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DeviceCard
      title={t("card.device.raw.header")}
      subTitle={t("card.device.raw.description")}
      topRight={
        <Switch
          id="device-view-toggle"
          labelKey={t("switch.view.specifics.label")}
          returnBoolean
          onChange={(val: any) => setSelectedView(val ? "specifics" : "device")}
          sizes="md"
          labelPosition="left"
          color="secondary"
        />
      }
    >
      {pendingEdit && (
        <div className="-mt-4 mb-2 flex justify-end gap-2">
          <GenericButton
            text={t("button.action.apply")}
            size="xs"
            rounded="rounded-md"
            onClick={applyEdits}
          />
          <CircleButton
            icon={MdClose}
            variant="outline"
            size="xs"
            onClick={() => setPendingEdit(null)}
          />
        </div>
      )}
      <div
        className="flex w-full items-center justify-center overflow-auto"
        style={{ height: dimensions.contentHeight - 180 }}
      >
        {!loading ? (
          <JsonViewer
            data={selectedView === "device" ? jsonData : specificsData}
            onEdit={(updated) => {
              setPendingEdit({
                updated_src: updated.updated_src,
                mode: selectedView,
              });
            }}
          />
        ) : (
          <div className="flex items-center justify-center bg-opacity-50">
            <BounceLoader loading={loading} />
          </div>
        )}
      </div>
    </DeviceCard>
  );
};

export default DeviceRaw;
